import { XMarkIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useRef, useState } from "react";
import getClassName from "./utils/combineClassName";

export interface MultiSelectOptionI<T = string> {
  value: T;
  label: string;
}

interface MultiSelectDropdownProps<T> {
  options: MultiSelectOptionI<T>[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  value: MultiSelectOptionI<T>[];
  onChange: (value: MultiSelectOptionI<T>[]) => void;
  onCreateOption?: (inputValue: string) => void;
  placeholder?: string;
}

export default function MultiSelectDropdown<T>({
  options,
  label,
  required,
  disabled = false,
  value,
  onChange,
  onCreateOption,
  placeholder = "Select options...",
}: MultiSelectDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const toggleOption = (option: MultiSelectOptionI<T>) => {
    if (disabled) return;
    const newSelectedOptions = value.find((item) => item.value === option.value)
      ? value.filter((item) => item.value !== option.value)
      : [...value, option];

    onChange(newSelectedOptions);
  };

  const removeOption = (option: MultiSelectOptionI<T>) => {
    if (disabled) return;
    const newSelectedOptions = value.filter(
      (item) => item.value !== option.value,
    );

    onChange(newSelectedOptions);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setInputValue("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [inputValue, isOpen]);

  const filteredOptions = inputValue
    ? options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : options;

  const showCreateOption =
    inputValue &&
    !options.some(
      (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
    ) &&
    onCreateOption;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    const totalOptions = showCreateOption
      ? filteredOptions.length + 1
      : filteredOptions.length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const nextIndex = prev < totalOptions - 1 ? prev + 1 : prev;
          // Scroll into view if needed
          const element = document.querySelector(`[data-index="${nextIndex}"]`);
          element?.scrollIntoView({ block: "nearest" });
          return nextIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => {
          const nextIndex = prev > 0 ? prev - 1 : 0;
          // Scroll into view if needed
          const element = document.querySelector(`[data-index="${nextIndex}"]`);
          element?.scrollIntoView({ block: "nearest" });
          return nextIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex === filteredOptions.length && showCreateOption) {
            onCreateOption?.(inputValue);
            setInputValue("");
          } else if (selectedIndex < filteredOptions.length) {
            toggleOption(filteredOptions[selectedIndex]);
          }
        }
        break;
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
      )}
      <div className="relative w-full h-auto" ref={dropdownRef}>
        <div
          className={getClassName([
            "w-full px-4 pt-2 pb-1 border border-[#0F172A] bg-white rounded-md shadow-lg flex items-center flex-wrap overflow-y-auto cursor-pointer",
            disabled && "bg-[#FAFAFA] cursor-not-allowed",
            isOpen && "border-[#00A3E0]",
          ])}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {value.length > 0 ? (
            value.map((option) => (
              <span
                key={option?.value as React.Key}
                className={`bg-[#00A3E0] px-2 py-1 rounded-full flex items-center mr-2 mb-1 text-xs ${
                  disabled ? "bg-gray-300 text-gray-500" : "text-white"
                }`}
              >
                {option?.label}
                {!disabled && (
                  <XMarkIcon
                    className="ml-1 w-3 h-3 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeOption(option);
                    }}
                  />
                )}
              </span>
            ))
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full bg-white border border-[#0F172A] rounded-md mt-1 max-h-60 overflow-auto shadow-lg">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-4 py-2 border-b"
              placeholder="Type to search or create..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            />
            {filteredOptions.map((option, index) => {
              const isSelected = value.find(
                (item) => item?.value === option.value,
              );
              const isHighlighted = index === selectedIndex;

              return (
                <div
                  key={option?.value as React.Key}
                  data-index={index}
                  onClick={() => toggleOption(option)}
                  className={`cursor-pointer px-4 py-2 ${
                    isSelected
                      ? isHighlighted
                        ? "bg-blue-200" // Selected AND highlighted
                        : "bg-gray-200" // Just selected
                      : isHighlighted
                        ? "bg-blue-100" // Just highlighted
                        : "hover:bg-gray-100"
                  }`}
                >
                  {option?.label}
                </div>
              );
            })}
            {showCreateOption && (
              <div
                data-index={filteredOptions.length}
                className={`cursor-pointer px-4 py-2 text-blue-600 ${
                  selectedIndex === filteredOptions.length
                    ? "bg-blue-100"
                    : "hover:bg-gray-100"
                }`}
                onClick={() => {
                  onCreateOption(inputValue);
                  setInputValue("");
                }}
              >
                Create &quot;{inputValue}&quot;
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
